import { useState } from 'react';
import { ChevronDown, CreditCard, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AdminTimeRangePicker } from '@/components/AdminTimeRangePicker';
import { TimeRangeFilter } from '@/components/TimeRangeFilter';
import { cn } from '@/lib/utils';
import { financeCountryOptions, financeCurrencyOptions } from '../lib/financeColors';

/**
 * Financial Management page-level controls:
 * Currency · Date range · Creator country · Export / Reports.
 * Changing date or country updates every card on the page.
 */
export function FinanceFilters({
  currency,
  onCurrencyChange,
  range,
  onRangeChange,
  countryCode,
  onCountryCodeChange,
  availableCountryCodes = [],
  startDate,
  endDate,
  onExportCsv,
  exportDisabled,
}: {
  currency: string;
  onCurrencyChange: (currency: string) => void;
  range: string;
  onRangeChange: (range: string) => void;
  countryCode: string;
  onCountryCodeChange: (countryCode: string) => void;
  availableCountryCodes?: readonly string[];
  startDate?: string;
  endDate?: string;
  onExportCsv: () => void;
  exportDisabled?: boolean;
}) {
  const [exportOpen, setExportOpen] = useState(false);

  const currencyOption = financeCurrencyOptions.find((option) => option.value === currency) ?? {
    value: currency,
    label: currency,
  };
  const countryOptions = financeCountryOptions(availableCountryCodes);
  const countryOption = countryOptions.find((option) => option.value === countryCode) ?? {
    value: '',
    label: 'All creator countries',
    detail: 'Connect country buckets + not yet onboarded · not buyer geo',
  };

  return (
    <div className="flex w-full flex-wrap items-center gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <CreditCard className="size-5 shrink-0 text-foreground" aria-hidden />
        <h1 className="text-base font-medium tracking-tight sm:text-lg">Financial Management</h1>
      </div>

      <div className="ml-auto flex flex-wrap items-center gap-2">
        <TimeRangeFilter
          size="compact"
          label={currencyOption.label}
          detail="Reporting currency"
          value={currency}
          options={financeCurrencyOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={onCurrencyChange}
          ariaLabel="Change reporting currency"
          className="w-[140px] sm:w-[160px]"
        />

        <AdminTimeRangePicker
          preset="finance"
          value={range}
          onChange={onRangeChange}
          {...(startDate ? { startDate } : {})}
          {...(endDate ? { endDate } : {})}
          ariaLabel="Change finance date range"
          className="w-full min-w-[200px] sm:w-[260px]"
        />

        <TimeRangeFilter
          size="compact"
          label={countryOption.label}
          detail="Creator country (Connect)"
          value={countryCode}
          options={countryOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          onChange={onCountryCodeChange}
          ariaLabel="Change creator country (Connect) filter"
          className="w-full min-w-[200px] sm:w-[260px]"
          title={countryOption.detail}
        />

        <Popover open={exportOpen} onOpenChange={setExportOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              disabled={exportDisabled}
              aria-label="Export / Reports"
              className={cn(
                'flex h-[49px] w-full items-center justify-between rounded-lg border border-[#272729] bg-[#101010] px-3 text-left outline-none transition-colors sm:w-[180px]',
                exportOpen && 'rounded-t-lg border-[#0194F5] border-b-transparent',
                !exportDisabled && 'hover:border-[#504F4E]',
                exportDisabled && 'opacity-50',
              )}
            >
              <span className="truncate text-sm text-white">Export / Reports</span>
              <ChevronDown
                className={cn('ml-2 size-4 shrink-0 text-white transition-transform', exportOpen && 'rotate-180')}
                aria-hidden
              />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-56 space-y-1 p-1.5">
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
              onClick={() => {
                setExportOpen(false);
                onExportCsv();
              }}
            >
              <Download className="size-4" aria-hidden />
              Export CSV
            </button>
            <button
              type="button"
              disabled
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted-foreground opacity-60"
            >
              <FileSpreadsheet className="size-4" aria-hidden />
              Excel · Not ready
            </button>
            <button
              type="button"
              disabled
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-muted-foreground opacity-60"
            >
              <FileText className="size-4" aria-hidden />
              PDF · Not ready
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
